/* =============================================================================
   VIEWS / ADMIN / MENU-CATEGORIES-ADMIN — Rubriken-Editor + Live-Gast-Vorschau
   =============================================================================
   S186-Rework: geteiltes Layout — links pflegen (Karten je Rubrik mit Drag-Handle,
   Icon-Picker, Name, Zähler, aktiv-Toggle, Löschen + eingerückte Unter-Rubriken),
   RECHTS die echte Gast-Speisekarte live (nur kind='menu'). Reihenfolge per Drag &
   Drop UND ↑↓-Pfeilen (beide schreiben sortOrder). option/extra-Rubriken bekommen
   denselben Karten-Editor, aber keine Gast-Vorschau (sie erscheinen nicht im Menü).

   KERNPUNKT (Memory feedback_gg_non_destructive_render): Tippen/Toggle/Icon dürfen
   NICHT den ganzen Tab neu rendern — sie speichern debounced und zeichnen NUR die
   Vorschau-Pane neu; Editor-DOM + Fokus/Scroll bleiben. Nur strukturelle Aktionen
   (anlegen/löschen/sortieren) rendern die Editor-Panes neu (kein Text-Fokus aktiv).

   Vorschau-Gruppierung über die echte Gast-Quelle `itemsBySubcategoryFromMenu`
   (kein Re-Implement → kein Drift). Inline-Handler via dom-bindings (kein window.func).
   Kein DB-Touch (bestehende menuCategories + Menü). Hinter Feature-Flag ingredientsDb.
============================================================================ */

import { htmlEscape, showToast, fmtEUR, requireEditPermission } from '../../utils/helpers.js';
import { buildCategoryTree } from '../../core/menu-categories.js';

const KIND_SECTIONS = [
  { kind: 'menu',   title: 'Menü-Rubriken (gast-sichtbar)', addLabel: '+ Rubrik' },
  { kind: 'option', title: 'Optionen (Brötchen / Patty / Dressing)', addLabel: '+ Options-Gruppe' },
  { kind: 'extra',  title: 'Extras (Toppings)', addLabel: '+ Extra-Gruppe' }
];

// Gängige Food-Emojis für den Icon-Picker (Datenfeld bleibt `icon` = Emoji-String).
const ICON_CHOICES = ['🍔','🍟','🥗','🍕','🌮','🥙','🍗','🍖','🥪','🌯','🍝','🍜','🥤','🍺','🍷','☕','🍰','🍦','🧀','🥑','🍳','🌭','🥨','🍩'];

let _cats = [];          // flache Rubriken-Liste (camelCase)
let _menu = [];          // Menü-Items (für Vorschau + Gericht-Zähler)
let _dragId = null;      // aktuell gezogene Rubrik-ID
let _dragParentKey = null;
let _dishDragId = null;  // S187 Cut A — aktuell gezogenes Gericht
let _dishSrcKey = null;  // Quell-Container "catId|subId"
const _saveTimers = {};  // debounce je id+feld
// S217 Cut 1 — welche Top-Rubrik-Karten sind aufgeklappt (per id). Modul-State
// (nicht DOM), damit ein Re-Render den Collapse-Zustand nicht zurücksetzt
// (gg-non-destructive-render). Default: alle eingeklappt (nur Kopfzeile).
const _expandedCards = new Set();

function flagOn() {
  return !!(window.AppConfig && window.AppConfig.features && window.AppConfig.features.ingredientsDb);
}
function storage() { return window.App && window.App.storage; }
function menuState() {
  const st = storage() && storage()._getInternalState && storage()._getInternalState();
  return (st && st.menu) || [];
}

/* -------------------------------------------------------------------------- */
/* Pure Helfer (Vitest)                                                        */
/* -------------------------------------------------------------------------- */

/**
 * Verschiebt ein Element von fromIdx nach toIdx (neue Reihenfolge). Pure —
 * Grundlage für Drag-Drop UND ↑↓ (beide erzeugen die neue sortOrder-Liste).
 */
export function reindexAfterDrop(list, fromIdx, toIdx) {
  const arr = Array.isArray(list) ? list.slice() : [];
  if (fromIdx < 0 || fromIdx >= arr.length || toIdx < 0 || toIdx >= arr.length || fromIdx === toIdx) return arr;
  const [moved] = arr.splice(fromIdx, 1);
  arr.splice(toIdx, 0, moved);
  return arr;
}

/**
 * Gehört ein Item zur Top-Rubrik `cat`? Bevorzugt die STABILE `categoryId`
 * (so wirkt eine Umbenennung sofort, ohne Gericht-Verknüpfung zu verlieren),
 * Fallback auf den Namen für Altdaten ohne `category_id`. Pure.
 */
export function itemInCategory(m, cat) {
  if (!m || !cat) return false;
  if (m.categoryId) return m.categoryId === cat.id;
  return m.category === cat.name;
}
/** Gehört ein Item zur Unter-Rubrik `sub`? (subcategoryId, Fallback Name). Pure. */
export function itemInSub(m, sub) {
  if (!m || !sub) return false;
  if (m.subcategoryId) return m.subcategoryId === sub.id;
  return (m.subcategory || '') === sub.name;
}

/** Anzahl Gerichte (inkl. inaktiver) einer Menü-Rubrik — ohne Options-Items.
 *  `cat` = {id, name}. ID-basiert (umbenennungsfest). Pure. */
export function countDishes(menu, cat) {
  return (Array.isArray(menu) ? menu : [])
    .filter(m => m && m.itemType !== 'option' && itemInCategory(m, cat)).length;
}

/**
 * Datenaufbereitung der Live-Vorschau — TREE-getrieben (aus den editierten
 * Rubrik-Records), Gerichte je Rubrik/Unter-Rubrik über die STABILE id verknüpft,
 * Labels aus dem Live-Baum. Dadurch zieht jede Editor-Änderung (umbenennen,
 * sortieren, aktiv, Icon) sofort in der Vorschau nach. Gefiltert wie der Gast:
 * nur verfügbare Nicht-Options-Gerichte. Inaktive Rubrik bleibt erhalten (wird
 * als „ausgeblendet" markiert, nicht entfernt). Pure (Vitest).
 */
export function buildPreviewModel(cats, menu) {
  const list = Array.isArray(menu) ? menu : [];
  const tree = buildCategoryTree((Array.isArray(cats) ? cats : []).filter(c => c.kind === 'menu'));
  const toDish = it => ({ id: it.id, name: it.name, price: Number(it.price) || 0 });
  return tree.map(t => {
    // S187 Cut-A-Fix — nach displayOrder sortieren, sonst zieht die Vorschau nach
    // einem Reorder nicht nach (menu.reorder ändert nur die Eigenschaft, nicht die
    // Array-Position im In-Memory-Cache). Wie im Gast-Pfad (itemsByCategoryFromMenu).
    const catItems = list
      .filter(m => m && m.available && m.itemType !== 'option' && itemInCategory(m, t))
      .sort((a, b) => (Number(a.displayOrder) || 0) - (Number(b.displayOrder) || 0));
    const placed = new Set();
    const subGroups = (t.children || []).map(c => {
      const items = catItems.filter(m => itemInSub(m, c));
      items.forEach(m => placed.add(m));
      // active je Unter-Rubrik mitgeben → inaktive Unter-Rubrik wird in der
      // Vorschau ausgegraut (so wie der Aktiv-Schalter auf Rubrik-Ebene).
      return { label: c.name || '', active: c.active !== false, items: items.map(toDish) };
    }).filter(g => g.items.length);
    const direct = catItems.filter(m => !placed.has(m));   // ohne (passende) Unter-Rubrik
    const groups = [];
    if (direct.length) groups.push({ label: '', active: true, items: direct.map(toDish) });
    groups.push(...subGroups);
    return {
      id: t.id,
      name: t.name,
      icon: t.icon || '',
      active: t.active !== false,
      subCount: (t.children || []).length,
      dishCount: catItems.length,
      groups
    };
  });
}

/**
 * S187 Cut A — Gerichte eines „Containers" (Unter-Rubrik ODER direkt unter der
 * Top-Rubrik, wenn sub=null), sortiert nach displayOrder. INKL. nicht-verfügbarer
 * (im Editor ausgegraut) — nur Options-Items raus. Pure (Vitest).
 *   sub=null  → Gerichte der Top-Rubrik OHNE Unter-Rubrik-Zuordnung.
 *   sub={id,name} → Gerichte dieser Unter-Rubrik.
 */
export function containerDishes(menu, cat, sub) {
  const list = (Array.isArray(menu) ? menu : []).filter(m =>
    m && m.itemType !== 'option' && itemInCategory(m, cat) &&
    (sub ? itemInSub(m, sub) : !(m.subcategoryId || m.subcategory)));
  return list.slice().sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));
}

/**
 * S187 Cut A — Item aus Quell-Container in Ziel-Container an Position dstIdx
 * verschieben. Rein index-basiert (ids): entfernt itemId aus src, fügt es an
 * dstIdx in dst ein (geklemmt). Gleicher Container möglich (src==dst-Referenz
 * NICHT nötig; Aufrufer übergibt getrennte id-Listen). Pure (Vitest).
 * @returns {{ src: string[], dst: string[] }}
 */
export function moveItemAcrossContainers(srcIds, dstIds, itemId, dstIdx) {
  const src = (Array.isArray(srcIds) ? srcIds : []).filter(x => x !== itemId);
  const dst = (Array.isArray(dstIds) ? dstIds : []).filter(x => x !== itemId);
  let i = Number.isInteger(dstIdx) ? dstIdx : dst.length;
  i = Math.max(0, Math.min(i, dst.length));
  dst.splice(i, 0, itemId);
  return { src, dst };
}

/* -------------------------------------------------------------------------- */
/* Render — Top-Level                                                          */
/* -------------------------------------------------------------------------- */

export async function renderMenuCategoriesAdmin() {
  const tab = document.getElementById('tab-categories');
  if (!tab) return;
  if (!flagOn()) { tab.innerHTML = ''; return; }
  _closeIconPopover();   // evtl. offenes Popover + Document-Listener vor Full-Render aufräumen
  tab.innerHTML = `<div class="card"><p class="hint">Lade Rubriken…</p></div>`;
  try {
    _cats = await storage().menuCategories.list();
  } catch (e) {
    console.warn('[categories] load:', e);
    _cats = [];
  }
  _menu = menuState();

  tab.innerHTML = `
    <div class="card" style="margin-bottom:14px;">
      <h2 style="margin:0;">Rubriken</h2>
      <p class="hint" style="margin:4px 0 0;">Links pflegen, rechts läuft die echte Gast-Speisekarte mit.
      Reihenfolge per Ziehen am Griff ⠿ oder ↑↓. Der Aktiv-Schalter blendet eine Rubrik beim Gast aus,
      ohne sie zu löschen.</p>
    </div>

    <div class="mcadmin-split">
      <div class="card" id="mcadmin-editor-menu">${_editorKindInner('menu')}</div>
      <div class="card" id="mcadmin-preview">${_previewInner()}</div>
    </div>
  `;
  // S188-2 — Options-/Extra-Rubriken werden NICHT mehr hier gepflegt: der neue
  // Optionssätze-Editor (Hub-Ansicht „Optionssätze") ist die Single-Source dafür.
  _wireDnd(tab);
  _wireDishDnd(tab);
}

/* -------------------------------------------------------------------------- */
/* Editor (links) — Karten je Rubrik                                           */
/* -------------------------------------------------------------------------- */

function _editorKindInner(kind) {
  const sec = KIND_SECTIONS.find(s => s.kind === kind) || KIND_SECTIONS[0];
  const tops = buildCategoryTree(_cats).filter(t => t.kind === kind);
  const cards = tops.map((t, i) => _catCardTop(t, kind, i, tops.length)).join('')
    || `<div class="hint" style="padding:6px 0;">Noch keine.</div>`;
  return `
    <div style="display:flex;align-items:baseline;justify-content:space-between;gap:10px;margin-bottom:10px;">
      <h3 style="margin:0;">${htmlEscape(sec.title)}</h3>
      <span class="mcadmin-count">${tops.length} ${tops.length === 1 ? 'Rubrik' : 'Rubriken'}</span>
    </div>
    ${cards}
    <button class="btn btn-secondary btn-sm" style="margin-top:8px;" onclick="catAddTop('${kind}')">${htmlEscape(sec.addLabel)}</button>
  `;
}

function _iconPickerHtml(node) {
  const id = htmlEscape(node.id);
  const cur = node.icon ? htmlEscape(node.icon) : '▦';
  const grid = ICON_CHOICES.map(e => `<button type="button" title="${e}" onclick="catPickIcon('${id}','${e}')">${e}</button>`).join('');
  return `
    <span class="mcadmin-iconwrap">
      <button type="button" class="mcadmin-iconbtn" id="mcadmin-iconbtn-${id}" title="Icon wählen" onclick="catIconToggle('${id}')">${cur}</button>
      <span class="mcadmin-iconpop" id="mcadmin-iconpop-${id}" data-open="0" style="display:none;">
        <span class="mcadmin-iconpop-grid">${grid}
          <button type="button" title="kein Icon" onclick="catPickIcon('${id}','')">∅</button>
        </span>
        <input type="text" value="${node.icon ? htmlEscape(node.icon) : ''}" placeholder="eigenes Emoji…" oninput="catIconInput('${id}', this.value)">
      </span>
    </span>`;
}

function _moveButtons(id, idx, total) {
  return `
    <span class="mcadmin-actions">
      <button class="btn btn-ghost btn-xs" title="nach oben" ${idx === 0 ? 'disabled' : ''} onclick="catMoveLive('${htmlEscape(id)}','up')">↑</button>
      <button class="btn btn-ghost btn-xs" title="nach unten" ${idx === total - 1 ? 'disabled' : ''} onclick="catMoveLive('${htmlEscape(id)}','down')">↓</button>
    </span>`;
}

/* --- S187 Cut A — Gerichte je (Unter-)Rubrik ------------------------------- */

/** Darf der aktuelle User die Speisekarte editieren? (Staff → read-only). */
function _canEditMenu() {
  const auth = (typeof window !== 'undefined' && window.App && window.App.auth) ? window.App.auth : null;
  if (!auth || typeof auth.canEdit !== 'function') return true; // Pre-Bootstrap/Tests: nicht blockieren
  return auth.canEdit('menu');
}

function _dishMoveButtons(id, idx, total) {
  return `
    <span class="mcadmin-actions">
      <button class="btn btn-ghost btn-xs" title="nach oben" ${idx === 0 ? 'disabled' : ''} onclick="dishMoveLive('${htmlEscape(id)}','up')">↑</button>
      <button class="btn btn-ghost btn-xs" title="nach unten" ${idx === total - 1 ? 'disabled' : ''} onclick="dishMoveLive('${htmlEscape(id)}','down')">↓</button>
    </span>`;
}

/** Eine Gericht-Zeile. Preis nur ANZEIGEN (Cut A). canEdit=false → read-only. */
function _dishRow(m, catId, subId, idx, total, canEdit) {
  const id = htmlEscape(m.id);
  const unavail = m.available === false;
  return `
    <div class="mcadmin-dishrow${unavail ? ' is-unavailable' : ''}" data-mc-dish="${id}"
         data-mc-cat="${htmlEscape(catId)}" data-mc-sub="${htmlEscape(subId || '')}">
      ${canEdit
        ? `<span class="mcadmin-dish-handle" draggable="true" title="Ziehen zum Sortieren/Verschieben">⠿</span>`
        : `<span class="mcadmin-dish-handle" style="opacity:.3;cursor:default;">⠿</span>`}
      <input class="mcadmin-name mcadmin-dish-name" type="text" value="${htmlEscape(m.name || '')}"
             ${canEdit ? '' : 'disabled'} placeholder="Gericht-Name" oninput="dishRenameInput('${id}', this.value)">
      <span class="mcadmin-dish-price" title="Preis (im Detail-Panel editierbar)">${fmtEUR(m.price)}</span>
      <span class="mcadmin-rowtools">
        ${canEdit ? _dishMoveButtons(m.id, idx, total) : ''}
        <label class="checkbox" style="font-size:.78rem;white-space:nowrap;">
          <input type="checkbox" ${unavail ? '' : 'checked'} ${canEdit ? '' : 'disabled'} onchange="dishToggleAvailable('${id}', this.checked)"> verfügbar
        </label>
        <button class="btn btn-ghost btn-xs" title="Details (Rezept/Preis/Optionen)" onclick="dishEdit('${id}')">bearbeiten →</button>
      </span>
    </div>`;
}

/** Dish-Container (Drop-Ziel). subId='' = direkte Gerichte der Top-Rubrik. */
function _dishListHtml(catId, subId, dishes, canEdit) {
  const rows = dishes.length
    ? dishes.map((m, i) => _dishRow(m, catId, subId, i, dishes.length, canEdit)).join('')
    : `<div class="mcadmin-dish-empty">keine Gerichte</div>`;
  return `<div class="mcadmin-dishes" data-mc-cat="${htmlEscape(catId)}" data-mc-sub="${htmlEscape(subId || '')}">${rows}</div>`;
}

function _catCardTop(t, kind, idx, total) {
  const id = htmlEscape(t.id);
  const isMenu = kind === 'menu';
  const canEdit = _canEditMenu();
  const open = _expandedCards.has(t.id);   // S217 Cut 1 — Default eingeklappt
  const subCount = (t.children || []).length;
  const counter = isMenu
    ? `${subCount} ${subCount === 1 ? 'Unter-Rubrik' : 'Unter-Rubriken'} · ${countDishes(_menu, t)} Gerichte`
    : `${subCount} ${subCount === 1 ? 'Untergruppe' : 'Untergruppen'}`;
  // S187 Cut A — je Unter-Rubrik die Gerichte darunter (menu-kind); direkt der
  // Top-Rubrik zugeordnete Gerichte (ohne Unter-Rubrik) direkt unter den Kopf.
  const subs = (t.children || []).map((c, j) => {
    const row = _subRow(c, t.id, j, t.children.length);
    const dishes = isMenu ? _dishListHtml(t.id, c.id, containerDishes(_menu, t, c), canEdit) : '';
    return row + dishes;
  }).join('');
  const directDishes = isMenu ? containerDishes(_menu, t, null) : [];
  const directHtml = directDishes.length ? _dishListHtml(t.id, '', directDishes, canEdit) : '';
  const subAddLabel = isMenu ? '+ Unter-Rubrik' : '+ Untergruppe';
  // S217 Cut 1 — Karte standardmäßig eingeklappt: nur Kopfzeile (Name + Zähler +
  // Werkzeuge); Body (direkte Gerichte + Unter-Rubriken) erst nach Aufklappen im
  // DOM. Caret als eigener Button, damit ein Klick ins Namensfeld nicht togglet.
  const body = open ? `
      ${directHtml}
      <div class="mcadmin-subs">
        ${subs}
        <button class="btn btn-ghost btn-xs" style="margin-top:4px;" onclick="catAddSub('${id}')">${subAddLabel}</button>
      </div>` : '';
  return `
    <div class="mcadmin-card${open ? ' is-open' : ''}" data-mc-id="${id}" data-mc-parent="root:${kind}">
      <div class="mcadmin-card-head">
        <button type="button" class="mcadmin-collapse" data-cardid="${id}" title="${open ? 'einklappen' : 'aufklappen'}"
                aria-expanded="${open ? 'true' : 'false'}" onclick="catToggleCard(this)">${open ? '▾' : '▸'}</button>
        <span class="mcadmin-handle" draggable="true" title="Ziehen zum Sortieren">⠿</span>
        ${_iconPickerHtml(t)}
        <input class="mcadmin-name" type="text" value="${htmlEscape(t.name || '')}" placeholder="Rubrik-Name"
               oninput="catRenameInput('${id}', this.value)">
        <span class="mcadmin-count">${counter}</span>
        <span class="mcadmin-rowtools">
          ${_moveButtons(t.id, idx, total)}
          <label class="checkbox" style="font-size:.82rem;white-space:nowrap;">
            <input type="checkbox" ${t.active !== false ? 'checked' : ''} onchange="catToggleActiveLive('${id}', this.checked)"> aktiv
          </label>
          <button class="btn btn-ghost btn-xs" style="color:var(--danger);" title="löschen/deaktivieren" onclick="catRemoveLive('${id}')">✕</button>
        </span>
      </div>
      ${body}
    </div>`;
}

/** S217 Cut 1 — Top-Rubrik-Karte auf-/zuklappen (Modul-State). Liest die id aus
 *  dem data-Attribut des geklickten Carets; strukturelle Re-Render (kein Text-
 *  Fokus aktiv) → _renderEditorPanes (verdrahtet DnD neu). */
export function catToggleCard(el) {
  const id = el && el.dataset ? el.dataset.cardid : null;
  if (!id) return;
  if (_expandedCards.has(id)) _expandedCards.delete(id);
  else _expandedCards.add(id);
  _renderEditorPanes();
}

function _subRow(c, parentId, idx, total) {
  const id = htmlEscape(c.id);
  return `
    <div class="mcadmin-subrow" data-mc-id="${id}" data-mc-parent="${htmlEscape(parentId)}">
      <span class="mcadmin-handle" draggable="true" title="Ziehen zum Sortieren">⠿</span>
      <input class="mcadmin-name" type="text" value="${htmlEscape(c.name || '')}" placeholder="Unter-Rubrik"
             oninput="catRenameInput('${id}', this.value)">
      <span class="mcadmin-rowtools">
        ${_moveButtons(c.id, idx, total)}
        <label class="checkbox" style="font-size:.8rem;white-space:nowrap;">
          <input type="checkbox" ${c.active !== false ? 'checked' : ''} onchange="catToggleActiveLive('${id}', this.checked)"> aktiv
        </label>
        <button class="btn btn-ghost btn-xs" style="color:var(--danger);" onclick="catRemoveLive('${id}')">✕</button>
      </span>
    </div>`;
}

/* -------------------------------------------------------------------------- */
/* Live-Vorschau (rechts) — spiegelt die Gast-Speisekarte                      */
/* -------------------------------------------------------------------------- */

function _previewInner() {
  const model = buildPreviewModel(_cats, _menu);
  const head = `<h3 style="margin:0 0 10px;">Gast-Vorschau</h3>`;
  if (!model.length) {
    return head + `<div class="mcadmin-prev-empty">Noch keine Menü-Rubriken — links eine anlegen.</div>`;
  }
  const blocks = model.map(b => {
    const counter = `${b.subCount} · ${b.dishCount}`;
    const dishes = b.groups.length
      ? b.groups.map(g => {
          const dimmed = g.active === false;
          const head = g.label
            ? `<div class="mcadmin-prev-sub">${htmlEscape(g.label)}${dimmed ? ' <span class="mcadmin-badge-hidden">ausgeblendet</span>' : ''}</div>`
            : '';
          const rows = g.items.length
            ? g.items.map(it => `
                <div class="mcadmin-prev-dish">
                  <span>${htmlEscape(it.name || '')}</span>
                  <span class="mcadmin-prev-dish-price">${fmtEUR(it.price)}</span>
                </div>`).join('')
            : `<div class="mcadmin-prev-nodish">noch keine Gerichte</div>`;
          return `<div class="mcadmin-prev-group${dimmed ? ' is-hidden' : ''}">${head}${rows}</div>`;
        }).join('')
      : `<div class="mcadmin-prev-nodish">noch keine Gerichte</div>`;
    return `
      <details class="mcadmin-prev-cat ${b.active ? '' : 'is-hidden'}" ${b.active ? 'open' : ''}>
        <summary>
          <span>${b.icon ? htmlEscape(b.icon) + ' ' : ''}${htmlEscape(b.name || '')}</span>
          <span class="mcadmin-badge">${counter}</span>
          ${b.active ? '' : '<span class="mcadmin-badge-hidden">ausgeblendet</span>'}
        </summary>
        <div class="mcadmin-prev-body">${dishes}</div>
      </details>`;
  }).join('');
  return head + blocks;
}

/* -------------------------------------------------------------------------- */
/* Partielle Re-Renders (non-destruktiv)                                       */
/* -------------------------------------------------------------------------- */

function _renderPreviewPane() {
  const el = document.getElementById('mcadmin-preview');
  if (el) el.innerHTML = _previewInner();
}

/** Editor-Panes (alle kinds) + Vorschau neu zeichnen — NUR bei strukturellen
 *  Aktionen (sortieren/anlegen/löschen), wenn kein Text-Input fokussiert ist. */
function _renderEditorPanes() {
  _closeIconPopover();   // offenes Popover + Listener vor dem DOM-Austausch wegräumen
  // S188-2 — nur noch die Menü-Rubriken hier; option/extra leben im Optionssätze-Editor.
  ['menu'].forEach(k => {
    const el = document.getElementById('mcadmin-editor-' + k);
    if (el) el.innerHTML = _editorKindInner(k);
  });
  _renderPreviewPane();
  const tab = document.getElementById('tab-categories');
  _wireDnd(tab);
  _wireDishDnd(tab);
}

async function _reloadAndRerender() {
  try { _cats = await storage().menuCategories.list(); }
  catch (e) { console.warn('[categories] reload:', e); }
  _menu = menuState();
  _renderEditorPanes();
}

function _debouncedSave(key, fn, ms) {
  if (_saveTimers[key]) clearTimeout(_saveTimers[key]);
  _saveTimers[key] = setTimeout(() => { _saveTimers[key] = null; fn(); }, ms || 450);
}

/* -------------------------------------------------------------------------- */
/* Drag & Drop (imperativ — dom-bindings kennt keine drag-events)              */
/* -------------------------------------------------------------------------- */

function _siblingIds(parentKey) {
  const tree = buildCategoryTree(_cats);
  if (typeof parentKey === 'string' && parentKey.startsWith('root:')) {
    const kind = parentKey.slice(5);
    return tree.filter(t => t.kind === kind).map(t => t.id);
  }
  const parent = tree.find(t => t.id === parentKey);
  return parent ? (parent.children || []).map(c => c.id) : [];
}

function _applyLocalOrder(ids) {
  ids.forEach((id, i) => {
    const cat = _cats.find(c => c.id === id);
    if (cat) cat.sortOrder = (i + 1) * 10;
  });
}

async function _persistOrder(ids) {
  try {
    await storage().menuCategories.reorder(ids);
  } catch (e) {
    showToast('Sortieren fehlgeschlagen: ' + (e.message || e), 'error');
    await _reloadAndRerender();
  }
}

function _wireDnd(root) {
  if (!root) return;
  root.querySelectorAll('.mcadmin-handle[draggable="true"]').forEach(h => {
    h.addEventListener('dragstart', (e) => {
      const card = h.closest('[data-mc-id]');
      if (!card) return;
      _dragId = card.dataset.mcId;
      _dragParentKey = card.dataset.mcParent;
      if (e.dataTransfer) { e.dataTransfer.effectAllowed = 'move'; try { e.dataTransfer.setData('text/plain', _dragId); } catch (_) { /* ie */ } }
      card.classList.add('mcadmin-dragging');
    });
    h.addEventListener('dragend', () => {
      _dragId = null; _dragParentKey = null;
      root.querySelectorAll('.mcadmin-dragging, .mcadmin-dragover')
        .forEach(el => el.classList.remove('mcadmin-dragging', 'mcadmin-dragover'));
    });
  });
  root.querySelectorAll('[data-mc-id]').forEach(card => {
    card.addEventListener('dragover', (e) => {
      if (!_dragId || card.dataset.mcParent !== _dragParentKey || card.dataset.mcId === _dragId) return;
      e.preventDefault();
      if (e.dataTransfer) e.dataTransfer.dropEffect = 'move';
      card.classList.add('mcadmin-dragover');
    });
    card.addEventListener('dragleave', () => card.classList.remove('mcadmin-dragover'));
    card.addEventListener('drop', (e) => {
      card.classList.remove('mcadmin-dragover');
      const targetId = card.dataset.mcId;
      const parentKey = card.dataset.mcParent;
      if (!_dragId || parentKey !== _dragParentKey || targetId === _dragId) return;
      e.preventDefault();
      const ids = _siblingIds(parentKey);
      const from = ids.indexOf(_dragId), to = ids.indexOf(targetId);
      if (from < 0 || to < 0) return;
      const newIds = reindexAfterDrop(ids, from, to);
      _applyLocalOrder(newIds);
      _renderEditorPanes();           // sofortiges Feedback (kein Reload)
      _persistOrder(newIds);          // im Hintergrund persistieren
    });
  });
}

/* --- S187 Cut A — Gericht-Drag (Reorder innerhalb + Move zwischen Rubriken) - */

/** Gericht-IDs eines Containers (catId + subId|''), sortiert nach displayOrder. */
function _containerDishIds(catId, subId) {
  const cat = _cats.find(c => c.id === catId) || { id: catId };
  const sub = subId ? (_cats.find(c => c.id === subId) || { id: subId }) : null;
  return containerDishes(_menu, cat, sub).map(d => d.id);
}

function _applyLocalDishOrder(ids) {
  ids.forEach((id, i) => {
    const it = _menu.find(m => m.id === id);
    if (it) it.displayOrder = (i + 1) * 10;
  });
}

/** Drop eines Gerichts auf Ziel-Container an Position von targetDishId (oder ans Ende). */
async function _applyDishDrop(dstCat, dstSub, targetDishId) {
  if (!_dishDragId) return;
  const [srcCat, srcSubRaw] = String(_dishSrcKey || '').split('|');
  const srcSub = srcSubRaw || '';
  const dstSubN = dstSub || '';
  const same = (srcCat === dstCat && srcSub === dstSubN);
  const dstIds = _containerDishIds(dstCat, dstSubN || null);
  const dropIdx = targetDishId ? dstIds.indexOf(targetDishId) : dstIds.length;

  if (same) {
    const from = dstIds.indexOf(_dishDragId);
    if (from < 0) return;
    const to = dropIdx < 0 ? dstIds.length - 1 : dropIdx;
    const newIds = reindexAfterDrop(dstIds, from, to);
    _applyLocalDishOrder(newIds);
    _renderEditorPanes();
    try { await storage().menu.reorder(newIds); }
    catch (e) { showToast('Sortieren fehlgeschlagen: ' + (e.message || e), 'error'); await _reloadAndRerender(); }
    return;
  }

  // Cross-Container: Item bekommt categoryId/subcategoryId des Ziels + Position.
  const srcIds = _containerDishIds(srcCat, srcSub || null);
  const { src, dst } = moveItemAcrossContainers(srcIds, dstIds, _dishDragId, dropIdx < 0 ? dstIds.length : dropIdx);
  const item = _menu.find(m => m.id === _dishDragId);
  if (item) {
    item.categoryId = dstCat;
    item.subcategoryId = dstSubN || null;
    if (!dstSubN) item.subcategory = '';   // Trigger spiegelt Namen; lokal leeren
  }
  _applyLocalDishOrder(dst);
  _applyLocalDishOrder(src);
  _renderEditorPanes();
  try {
    // Zuordnung setzen; ohne Ziel-Unter-Rubrik auch den (Trigger-gespiegelten)
    // subcategory-String leeren — analog updateMenuSubcategoryId (menu-editor.js).
    const changes = dstSubN
      ? { categoryId: dstCat, subcategoryId: dstSubN }
      : { categoryId: dstCat, subcategoryId: null, subcategory: '' };
    await storage().menu.update(_dishDragId, changes);
    await storage().menu.reorder(dst);
    if (src.length) await storage().menu.reorder(src);
  } catch (e) {
    showToast('Verschieben fehlgeschlagen: ' + (e.message || e), 'error');
    await _reloadAndRerender();
  }
}

function _wireDishDnd(root) {
  if (!root) return;
  root.querySelectorAll('.mcadmin-dish-handle[draggable="true"]').forEach(h => {
    h.addEventListener('dragstart', (e) => {
      const row = h.closest('[data-mc-dish]');
      if (!row) return;
      _dishDragId = row.dataset.mcDish;
      _dishSrcKey = row.dataset.mcCat + '|' + (row.dataset.mcSub || '');
      if (e.dataTransfer) { e.dataTransfer.effectAllowed = 'move'; try { e.dataTransfer.setData('text/plain', _dishDragId); } catch (_) { /* ie */ } }
      row.classList.add('mcadmin-dragging');
      e.stopPropagation();   // nicht als Rubrik-Drag interpretieren
    });
    h.addEventListener('dragend', () => {
      _dishDragId = null; _dishSrcKey = null;
      root.querySelectorAll('.mcadmin-dishrow.mcadmin-dragging, .mcadmin-dishes.mcadmin-dragover, .mcadmin-dishrow.mcadmin-dragover')
        .forEach(el => el.classList.remove('mcadmin-dragging', 'mcadmin-dragover'));
    });
  });
  // Drop-Ziele: einzelne Gericht-Zeilen (Position) UND der Container (ans Ende / leere Rubrik).
  const overOk = (el, e) => {
    if (!_dishDragId) return false;
    e.preventDefault();
    if (e.dataTransfer) e.dataTransfer.dropEffect = 'move';
    return true;
  };
  root.querySelectorAll('.mcadmin-dishrow').forEach(row => {
    row.addEventListener('dragover', (e) => { if (overOk(row, e)) row.classList.add('mcadmin-dragover'); });
    row.addEventListener('dragleave', () => row.classList.remove('mcadmin-dragover'));
    row.addEventListener('drop', (e) => {
      row.classList.remove('mcadmin-dragover');
      if (!_dishDragId || row.dataset.mcDish === _dishDragId) return;
      e.preventDefault(); e.stopPropagation();
      _applyDishDrop(row.dataset.mcCat, row.dataset.mcSub || '', row.dataset.mcDish);
    });
  });
  root.querySelectorAll('.mcadmin-dishes').forEach(box => {
    box.addEventListener('dragover', (e) => { if (overOk(box, e)) box.classList.add('mcadmin-dragover'); });
    box.addEventListener('dragleave', () => box.classList.remove('mcadmin-dragover'));
    box.addEventListener('drop', (e) => {
      box.classList.remove('mcadmin-dragover');
      if (!_dishDragId) return;
      // Nur behandeln, wenn nicht schon eine Zeile den Drop konsumiert hat.
      if (e.defaultPrevented) return;
      e.preventDefault();
      _applyDishDrop(box.dataset.mcCat, box.dataset.mcSub || '', null);  // ans Ende
    });
  });
}

/* -------------------------------------------------------------------------- */
/* Handler (window-registriert, CSP: func() ohne window.-Prefix)               */
/* -------------------------------------------------------------------------- */

export async function catAddTop(kind) {
  const name = ((await window.styledPrompt({ title: 'Neue Rubrik', label: 'Name der Rubrik', placeholder: 'z. B. Burger' })) || '').trim();
  if (!name) return;
  const sibs = buildCategoryTree(_cats).filter(t => t.kind === kind);
  try {
    await storage().menuCategories.create({ name, kind, parentId: null, sortOrder: (sibs.length + 1) * 10, active: true });
    showToast('Rubrik angelegt', 'success');
    await _reloadAndRerender();
  } catch (e) { showToast('Anlegen fehlgeschlagen: ' + (e.message || e), 'error'); }
}

export async function catAddSub(parentId) {
  const parent = _cats.find(c => c.id === parentId);
  if (!parent) return;
  const name = ((await window.styledPrompt({ title: 'Neue Unter-Rubrik', label: `unter „${parent.name}"`, placeholder: 'Name' })) || '').trim();
  if (!name) return;
  const sibs = (buildCategoryTree(_cats).find(t => t.id === parentId) || {}).children || [];
  try {
    await storage().menuCategories.create({ name, kind: parent.kind, parentId, sortOrder: (sibs.length + 1) * 10, active: true });
    showToast('Unter-Rubrik angelegt', 'success');
    await _reloadAndRerender();
  } catch (e) { showToast('Anlegen fehlgeschlagen: ' + (e.message || e), 'error'); }
}

/** Tippen → lokal sofort (Vorschau zieht nach), Persistenz debounced. KEIN
 *  Editor-Re-Render (Fokus/Cursor bleiben). */
export function catRenameInput(id, value) {
  const cat = _cats.find(c => c.id === id);
  if (!cat) return;
  cat.name = value;
  _renderPreviewPane();
  _debouncedSave('name:' + id, async () => {
    const name = (value || '').trim();
    if (!name) return; // leeren Namen nicht persistieren (letzter guter Stand bleibt)
    try { await storage().menuCategories.update(id, { name }); }
    catch (e) { showToast('Umbenennen fehlgeschlagen: ' + (e.message || e), 'error'); }
  });
}

export async function catToggleActiveLive(id, checked) {
  const cat = _cats.find(c => c.id === id);
  if (!cat) return;
  cat.active = !!checked;
  _renderPreviewPane();   // aktiv aus → Rubrik wird in der Vorschau ausgegraut
  try { await storage().menuCategories.update(id, { active: !!checked }); }
  catch (e) { showToast('Speichern fehlgeschlagen: ' + (e.message || e), 'error'); }
}

/* --- S187 Cut A — Gericht-Handler ----------------------------------------- */

/** Gericht umbenennen: lokal sofort (Vorschau zieht nach), Persistenz debounced.
 *  KEIN Editor-Re-Render (Fokus/Cursor bleiben). */
export function dishRenameInput(id, value) {
  if (!_canEditMenu()) return;
  const it = _menu.find(m => m.id === id);
  if (!it) return;
  it.name = value;
  _renderPreviewPane();
  _debouncedSave('dishname:' + id, async () => {
    const name = (value || '').trim();
    if (!name) return;
    try { await storage().menu.update(id, { name }); }
    catch (e) { showToast('Umbenennen fehlgeschlagen: ' + (e.message || e), 'error'); }
  });
}

export async function dishToggleAvailable(id, checked) {
  if (!requireEditPermission('menu', 'Gericht ändern')) return;
  const it = _menu.find(m => m.id === id);
  if (!it) return;
  it.available = !!checked;
  _renderEditorPanes();   // Zeile ausgrauen + Gast-Vorschau blendet aus/ein
  try { await storage().menu.update(id, { available: !!checked }); }
  catch (e) { showToast('Speichern fehlgeschlagen: ' + (e.message || e), 'error'); await _reloadAndRerender(); }
}

export function dishMoveLive(id, dir) {
  if (!requireEditPermission('menu', 'Gericht sortieren')) return;
  const it = _menu.find(m => m.id === id);
  if (!it) return;
  const ids = _containerDishIds(it.categoryId, it.subcategoryId || null);
  const idx = ids.indexOf(id);
  if (idx < 0) return;
  const to = dir === 'up' ? idx - 1 : idx + 1;
  if (to < 0 || to >= ids.length) return;
  const newIds = reindexAfterDrop(ids, idx, to);
  _applyLocalDishOrder(newIds);
  _renderEditorPanes();
  storage().menu.reorder(newIds)
    .catch(e => { showToast('Sortieren fehlgeschlagen: ' + (e.message || e), 'error'); _reloadAndRerender(); });
}

/** „bearbeiten →" — bestehende Konfigurator-Tiefe als Slide-over re-hosten (S187 Cut B,
 *  EIN Code-Pfad, kein Zweit-Editor). Staff read-only → Gate. */
export function dishEdit(id) {
  if (!requireEditPermission('menu', 'Gericht bearbeiten')) return;
  if (typeof window.openConfigurator === 'function') { window.openConfigurator(id, { present: 'slideover' }); return; }
  if (typeof window.editMenuItem === 'function') { window.editMenuItem(id); return; }
  showToast('Detail-Editor nicht verfügbar.', 'error');
}

/** S187 Cut B — nach cfgSave aus dem Slide-over: Studio-Liste + Vorschau non-destruktiv
 *  neu zeichnen (Item-Cache ist bereits frisch; nur Panes/Preview neu, kein Tab-Reset). */
export function studioRefreshAfterItemSave() {
  _menu = menuState();
  _renderEditorPanes();
}

/* Icon-Popover: immer nur EINER offen; schließt per Außenklick, Escape, Enter
   (eigenes Emoji bestätigt) und nach Auswahl. Document-Listener werden beim
   Öffnen registriert und beim Schließen sauber wieder entfernt. */
let _openIconId = null;
let _iconPointerHandler = null;
let _iconKeyHandler = null;

function _closeIconPopover() {
  if (_openIconId != null) {
    const pop = document.getElementById('mcadmin-iconpop-' + _openIconId);
    if (pop) { pop.style.display = 'none'; pop.setAttribute('data-open', '0'); }
  }
  _openIconId = null;
  if (_iconPointerHandler) { document.removeEventListener('pointerdown', _iconPointerHandler, true); _iconPointerHandler = null; }
  if (_iconKeyHandler) { document.removeEventListener('keydown', _iconKeyHandler, true); _iconKeyHandler = null; }
}

function _openIconPopover(id) {
  _closeIconPopover();                       // Single-Open + alte Listener weg
  const pop = document.getElementById('mcadmin-iconpop-' + id);
  if (!pop) return;
  pop.style.display = 'block';
  pop.setAttribute('data-open', '1');
  _openIconId = id;
  const wrap = pop.closest('.mcadmin-iconwrap');
  _iconPointerHandler = (e) => {
    // Klick auf Trigger ODER innerhalb des Popovers → offen lassen; sonst schließen.
    if (wrap && wrap.contains(e.target)) return;
    _closeIconPopover();
  };
  _iconKeyHandler = (e) => {
    if (e.key === 'Escape') { _closeIconPopover(); return; }
    // Enter im Freitext-Feld = „eigenes Emoji bestätigt" → schließen.
    if (e.key === 'Enter' && e.target && typeof e.target.closest === 'function'
        && e.target.closest('#mcadmin-iconpop-' + id)) {
      e.preventDefault();
      _closeIconPopover();
    }
  };
  document.addEventListener('pointerdown', _iconPointerHandler, true);
  document.addEventListener('keydown', _iconKeyHandler, true);
}

export function catIconToggle(id) {
  if (_openIconId === id) { _closeIconPopover(); return; }
  _openIconPopover(id);
}

function _updateIconChip(id, icon) {
  const btn = document.getElementById('mcadmin-iconbtn-' + id);
  if (btn) btn.textContent = icon || '▦';
}

export function catPickIcon(id, emoji) {
  const cat = _cats.find(c => c.id === id);
  if (!cat) return;
  const v = (emoji || '').trim();
  cat.icon = v || null;
  _updateIconChip(id, v);
  _closeIconPopover();          // Auswahl schließt den Popover (+ Listener-Teardown)
  _renderPreviewPane();
  storage().menuCategories.update(id, { icon: v || null })
    .catch(e => showToast('Icon speichern fehlgeschlagen: ' + (e.message || e), 'error'));
}

export function catIconInput(id, value) {
  const cat = _cats.find(c => c.id === id);
  if (!cat) return;
  const v = (value || '').trim();
  cat.icon = v || null;
  _updateIconChip(id, v);
  _renderPreviewPane();
  _debouncedSave('icon:' + id, () =>
    storage().menuCategories.update(id, { icon: v || null })
      .catch(e => showToast('Icon speichern fehlgeschlagen: ' + (e.message || e), 'error')));
}

export function catMoveLive(id, dir) {
  const cat = _cats.find(c => c.id === id);
  if (!cat) return;
  const parentKey = cat.parentId ? cat.parentId : ('root:' + cat.kind);
  const ids = _siblingIds(parentKey);
  const idx = ids.indexOf(id);
  if (idx < 0) return;
  const to = dir === 'up' ? idx - 1 : idx + 1;
  if (to < 0 || to >= ids.length) return;
  const newIds = reindexAfterDrop(ids, idx, to);
  _applyLocalOrder(newIds);
  _renderEditorPanes();
  _persistOrder(newIds);
}

export async function catRemoveLive(id) {
  const cat = _cats.find(c => c.id === id);
  if (!cat) return;
  if (!(await window.styledConfirm({
    title: `Rubrik „${cat.name}" löschen?`,
    body: 'Wird sie noch von Gerichten genutzt, wird sie nur deaktiviert (nicht gelöscht).',
    danger: true, confirmLabel: 'Löschen'
  })).confirmed) return;
  try {
    const res = await storage().menuCategories.remove(id);
    showToast(res && res.deactivated
      ? `Noch ${res.itemCount} Gericht(e) zugeordnet → nur deaktiviert.`
      : 'Rubrik gelöscht', res && res.deactivated ? 'info' : 'success');
    await _reloadAndRerender();
  } catch (e) { showToast('Löschen fehlgeschlagen: ' + (e.message || e), 'error'); }
}

if (typeof window !== 'undefined') {
  Object.assign(window, {
    renderMenuCategoriesAdmin,
    catToggleCard,   // S217 Cut 1 — Rubrik-Karte auf-/zuklappen
    catAddTop, catAddSub, catRenameInput, catToggleActiveLive,
    catIconToggle, catPickIcon, catIconInput, catMoveLive, catRemoveLive,
    // S187 Cut A — Gericht-Handler
    dishRenameInput, dishToggleAvailable, dishMoveLive, dishEdit,
    // S187 Cut B — Slide-over-Rückkanal
    studioRefreshAfterItemSave
  });
}
